// ========================================
// גרף היסטוריה - History Chart Component
// ========================================
import React from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    ComposedChart
} from 'recharts';
import { Loader2, TrendingUp, Package, AlertTriangle } from 'lucide-react';

/**
 * גרף היסטוריה לפריט
 * @param {Array} data - נתוני היסטוריה מה-hook
 * @param {boolean} isLoading - האם בטעינה
 * @param {string} error - הודעת שגיאה (אם יש)
 */
const HistoryChart = ({ data, isLoading, error }) => {
    // מצב טעינה
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                <p className="text-sm">טוען היסטוריה...</p>
            </div>
        );
    }

    // מצב שגיאה
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-red-400">
                <AlertTriangle className="w-8 h-8 mb-3" />
                <p className="text-sm">{error}</p>
            </div>
        );
    }

    // אין נתונים
    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Package className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm font-medium">אין היסטוריה זמינה</p>
                <p className="text-xs text-slate-500 mt-1">
                    הנתונים יתחילו להצטבר לאחר מספר טעינות
                </p>
            </div>
        );
    }

    // פורמט תאריך לתצוגה
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
    };

    // Custom Tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 text-right" dir="rtl">
                    <p className="text-xs text-slate-500 mb-2">{formatDate(label)}</p>
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between gap-4 text-sm">
                            <span style={{ color: entry.color }}>{entry.name}</span>
                            <span className="font-bold">{entry.value}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="w-full space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
                <StatMini
                    label="ממוצע מלאי"
                    value={Math.round(data.reduce((sum, d) => sum + d.netStock, 0) / data.length)}
                    icon={Package}
                />
                <StatMini
                    label="ממוצע מכירות"
                    value={Math.round(data.reduce((sum, d) => sum + d.velocity, 0) / data.length)}
                    icon={TrendingUp}
                />
                <StatMini
                    label="נקודות נתונים"
                    value={data.length}
                    icon={TrendingUp}
                />
            </div>

            {/* Chart */}
            <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={data}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            axisLine={{ stroke: '#e2e8f0' }}
                        />
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            axisLine={{ stroke: '#e2e8f0' }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            axisLine={{ stroke: '#e2e8f0' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                        />

                        {/* מלאי נטו - Area */}
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="netStock"
                            name="מלאי נטו"
                            fill="#3b82f6"
                            fillOpacity={0.1}
                            stroke="#3b82f6"
                            strokeWidth={2}
                        />

                        {/* מכירות - Line */}
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="velocity"
                            name="מכירות (חודשי)"
                            stroke="#10b981"
                            strokeWidth={2}
                            dot={{ r: 3, fill: '#10b981' }}
                            activeDot={{ r: 5 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Legend Explanation */}
            <div className="flex justify-center gap-6 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span>מלאי נטו (פיזי + נמל)</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span>מכירות חודשיות</span>
                </div>
            </div>
        </div>
    );
};

// Mini stat component for summary
const StatMini = ({ label, value, icon: Icon }) => (
    <div className="bg-slate-50 rounded-xl p-2 border border-slate-100">
        <div className="flex items-center justify-center gap-1 text-slate-400 text-[10px] mb-0.5">
            <Icon size={10} />
            <span>{label}</span>
        </div>
        <div className="text-base font-bold text-slate-700">{value}</div>
    </div>
);

export default HistoryChart;
